import React, { useState, useEffect, useRef } from 'react';
import '../../styles/jupiter-trading.css';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { useWallet } from '../../contexts/WalletContext';
import { useWalletBalance } from '../../hooks/useWalletBalance';
import SafeBalanceDisplay from '../common/SafeBalanceDisplay';
import BalanceValidator from '../common/BalanceValidator';
import tokenService from '../../services/tokenService';

const JupiterStyleTradingInterface = () => {
  const { tokens } = useAuth();
  const { wallets, activeWallet } = useWallet();
  const { showSuccess, showError, showTransaction } = useToast();
  
  // Trading state
  const [tradeType, setTradeType] = useState('buy'); // 'buy' or 'sell'
  const [fromToken, setFromToken] = useState({
    mint: 'So11111111111111111111111111111111111111112',
    symbol: 'SOL',
    name: 'Solana',
    decimals: 9,
    logoURI: 'https://raw.githubusercontent.com/solana-labs/token-list/main/assets/mainnet/So11111111111111111111111111111111111111112/logo.png',
    coingeckoId: 'solana'
  });
  const [toToken, setToToken] = useState(null);
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [slippage, setSlippage] = useState(0.5);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTokenSelector, setShowTokenSelector] = useState(false);
  const [tokenSelectorFor, setTokenSelectorFor] = useState('from'); // 'from' or 'to'
  const [searchQuery, setSearchQuery] = useState('');
  const [availableTokens, setAvailableTokens] = useState([]);
  const [userTokens, setUserTokens] = useState([]);
  const [isSearchingToken, setIsSearchingToken] = useState(false);
  const [searchPreviewToken, setSearchPreviewToken] = useState(null);
  const [searchPreviewPrice, setSearchPreviewPrice] = useState(null);
  const [searchError, setSearchError] = useState('');
  const [priceData, setPriceData] = useState(null);
  const [toTokenPrice, setToTokenPrice] = useState(null);
  const [inputFocus, setInputFocus] = useState('from'); // Track which input is being edited
  // Animation states
  const [isSwapping, setIsSwapping] = useState(false);
  const [showQuoteAnimation, setShowQuoteAnimation] = useState(false);
  
  // Use the new balance hook
  const {
    balance: walletBalance,
    isLoading: isRefreshingBalance,
    getTokenBalance,
    hasSufficientBalance,
    formatBalance: formatBalanceAmount,
    refreshBalance
  } = useWalletBalance();
  
  const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://safepumback.civicstech.name.ng/api';
  
  const storageKey = (walletId) => `safepump_user_tokens_${walletId || 'global'}`;

  const mergeUniqueByMint = (a, b) => {
    const byMint = new Map();
    [...a, ...b].forEach(t => {
      if (t && t.mint) {
        byMint.set(t.mint, t);
      }
    });
    return Array.from(byMint.values());
  };

  const loadUserTokens = (walletId) => {
    try {
      const raw = localStorage.getItem(storageKey(walletId));
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
      return [];
    } catch (e) {
      console.warn('Failed to load user tokens from storage', e);
      return [];
    }
  };

  const saveUserTokens = (walletId, tokensToSave) => {
    try {
      localStorage.setItem(storageKey(walletId), JSON.stringify(tokensToSave));
    } catch (e) {
      console.warn('Failed to save user tokens to storage', e);
    }
  };

  useEffect(() => {
    // Load tokens from service and user storage
    const popularTokens = tokenService.getPopularTokens();
    const loadedUserTokens = loadUserTokens(activeWallet?.id);
    setUserTokens(loadedUserTokens);
    setAvailableTokens(mergeUniqueByMint(loadedUserTokens, popularTokens));
    fetchTokenPrice();
    
    console.log('🚀 JupiterStyleTradingInterface loaded with enhanced features:');
    console.log('  ✅ Percentage buttons (25%, 50%, 75%, MAX)');
    console.log('  ✅ Real wallet balance fetching with useWalletBalance hook');
    console.log('  ✅ Clickable balance for max amount');
    console.log('  ✅ Balance refresh functionality');
    console.log('  ✅ Balance validation before trades');
  }, [activeWallet?.id]);

  useEffect(() => {
    if (fromToken) {
      fetchTokenPrice();
    }
  }, [fromToken]);

  useEffect(() => {
    if (toToken) {
      fetchToTokenPrice();
    }
  }, [toToken]);

  // Real-time price calculation effect
  useEffect(() => {
    if (fromAmount && fromToken && toToken && priceData && toTokenPrice) {
      calculateRealTimePrice();
    }
  }, [fromAmount, toAmount, fromToken, toToken, priceData, toTokenPrice, inputFocus]);

  // Quote effect for accurate trading
  useEffect(() => {
    if (fromAmount && fromToken && toToken && parseFloat(fromAmount) > 0) {
      const debounceTimer = setTimeout(() => {
        getQuote();
      }, 800);
      return () => clearTimeout(debounceTimer);
    }
  }, [fromAmount, fromToken, toToken, slippage]);

  const makeAuthenticatedRequest = async (url, options = {}) => {
    if (!tokens?.access_token) {
      throw new Error('No authentication token available');
    }

    const response = await fetch(`${API_BASE_URL}${url}`, {
      ...options,
      headers: {
        'Authorization': `Bearer ${tokens.access_token}`,
        'Content-Type': 'application/json',
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || `HTTP ${response.status}`);
    }

    return response.json();
  };

  const fetchTokenPrice = async () => {
    try {
      // Try DexScreener first for richer data like dashboard
      const dexData = await tokenService.getDexTokenData(fromToken?.mint || 'So11111111111111111111111111111111111111112');
      const priceData = dexData?.price != null ? dexData : await tokenService.getTokenPrice(fromToken?.mint || 'So11111111111111111111111111111111111111112');
      // If symbol/name are UNKNOWN, try to enrich from dex
      if (fromToken && (!fromToken.symbol || fromToken.symbol === 'UNKNOWN') && dexData?.symbol) {
        setFromToken({ ...fromToken, symbol: dexData.symbol, name: dexData.name || fromToken.name });
      }
      setPriceData(priceData);
    } catch (error) {
      console.error('Failed to fetch price data:', error);
    }
  };

  const fetchToTokenPrice = async () => {
    try {
      const dexData = await tokenService.getDexTokenData(toToken?.mint || 'So11111111111111111111111111111111111111112');
      const priceData = dexData?.price != null ? dexData : await tokenService.getTokenPrice(toToken?.mint || 'So11111111111111111111111111111111111111112');
      if (toToken && (!toToken.symbol || toToken.symbol === 'UNKNOWN') && dexData?.symbol) {
        setToToken({ ...toToken, symbol: dexData.symbol, name: dexData.name || toToken.name });
      }
      setToTokenPrice(priceData);
    } catch (error) {
      console.error('Failed to fetch to token price data:', error);
    }
  };

  // fetchWalletBalance function removed - now using useWalletBalance hook

  const calculateRealTimePrice = () => {
    if (!priceData || !toTokenPrice || !fromToken || !toToken) return;

    if (inputFocus === 'from' && fromAmount) {
      // Calculate toAmount based on fromAmount
      const fromValue = parseFloat(fromAmount) * priceData.price;
      const calculatedToAmount = fromValue / toTokenPrice.price;
      setToAmount(calculatedToAmount.toFixed(6));
    } else if (inputFocus === 'to' && toAmount) {
      // Calculate fromAmount based on toAmount
      const toValue = parseFloat(toAmount) * toTokenPrice.price;
      const calculatedFromAmount = toValue / priceData.price;
      setFromAmount(calculatedFromAmount.toFixed(6));
    }
  };

  const handleFromAmountChange = (value) => {
    setInputFocus('from');
    
    // Validate amount doesn't exceed available balance
    const availableBalance = getAvailableBalance(fromToken);
    const numericValue = parseFloat(value) || 0;
    
    if (numericValue > availableBalance && availableBalance > 0) {
      // Show warning but still allow the input (user might be testing)
      console.warn(`⚠️ Amount ${numericValue} exceeds available balance ${availableBalance}`);
    }
    
    setFromAmount(value);
  };

  const handleToAmountChange = (value) => {
    setInputFocus('to');
    setToAmount(value);
  };

  // getTokenBalance function replaced by useWalletBalance hook
  const getTokenBalanceForToken = (token) => {
    if (!token) return 0;
    return getTokenBalance(token.mint);
  };

  const setPercentageAmount = (percentage) => {
    console.log(`🎯 setPercentageAmount called with ${percentage}%`);
    const balance = getTokenBalanceForToken(fromToken);
    console.log(`💰 Current balance: ${balance} ${fromToken?.symbol || 'Unknown'}`);
    
    if (balance <= 0) {
      console.log('❌ No balance available');
      return;
    }
    
    let availableBalance = balance;
    
    // For SOL, reserve amount for transaction fees
    if (fromToken.symbol === 'SOL') {
      const estimatedFee = 0.01; // Estimate 0.01 SOL for transaction fees
      availableBalance = Math.max(0, balance - estimatedFee);
      console.log(`⛽ SOL fee reserved: ${estimatedFee}, available: ${availableBalance}`);
    }
    
    const amount = (availableBalance * percentage) / 100;
    if (amount > 0) {
      handleFromAmountChange(amount.toFixed(6));
      console.log(`✅ Set ${percentage}% of balance: ${amount.toFixed(6)} ${fromToken.symbol}`);
    } else {
      console.log('❌ Calculated amount is 0 or negative');
    }
  };

  const setMaxAmount = () => setPercentageAmount(100);
  const setHalfAmount = () => setPercentageAmount(50);
  const setQuarterAmount = () => setPercentageAmount(25);
  const setThreeQuarterAmount = () => setPercentageAmount(75);

  const getAvailableBalance = (token) => {
    const balance = getTokenBalanceForToken(token);
    if (token?.symbol === 'SOL') {
      const estimatedFee = 0.01;
      return Math.max(0, balance - estimatedFee);
    }
    return balance;
  };

  const getQuote = async () => {
    if (!fromAmount || !fromToken || !toToken || !activeWallet) {
      return;
    }

    try {
      setLoading(true);
      setShowQuoteAnimation(true);
      
      const inputAmount = tradeType === 'buy' 
        ? Math.floor(parseFloat(fromAmount) * Math.pow(10, fromToken.decimals))
        : Math.floor(parseFloat(fromAmount) * Math.pow(10, fromToken.decimals));

      const data = await makeAuthenticatedRequest('/trading/quote', {
        method: 'POST',
        body: JSON.stringify({
          inputMint: fromToken.mint,
          outputMint: toToken.mint,
          amount: inputAmount,
          slippageBps: Math.floor(slippage * 100)
        })
      });

      setQuote(data.data.quote);
      const outputAmount = data.data.quote.outputAmount / Math.pow(10, toToken.decimals);
      setToAmount(outputAmount.toFixed(6));
      
      setTimeout(() => setShowQuoteAnimation(false), 300);
    } catch (error) {
      showError(`Failed to get quote: ${error.message}`);
      setShowQuoteAnimation(false);
    } finally {
      setLoading(false);
    }
  };

  const swapTokens = () => {
    setIsSwapping(true);
    setTimeout(() => {
      const tempToken = fromToken;
      const tempAmount = fromAmount;
      
      setFromToken(toToken);
      setToToken(tempToken);
      setFromAmount(toAmount);
      setToAmount(tempAmount);
      setIsSwapping(false);
    }, 300);
  };

  const openTokenSelector = (type) => {
    setTokenSelectorFor(type);
    setShowTokenSelector(true);
    setSearchQuery('');
    setSearchPreviewToken(null);
    setSearchError('');
  };

  const selectToken = (token) => {
    if (tokenSelectorFor === 'from') {
      setFromToken(token);
    } else {
      setToToken(token);
    }
    setShowTokenSelector(false);
  };

  const handleAddTokenToUserList = (token) => {
    if (!token || !token.mint) return;
    const exists = userTokens.some(t => t.mint === token.mint);
    if (exists) {
      showSuccess('Token already in your list');
      return;
    }
    const updatedUserTokens = [...userTokens, token];
    setUserTokens(updatedUserTokens);
    saveUserTokens(activeWallet?.id, updatedUserTokens);
    // Merge into available tokens
    const popularTokens = tokenService.getPopularTokens();
    setAvailableTokens(mergeUniqueByMint(updatedUserTokens, popularTokens));
    showSuccess(`Added ${token.symbol || 'Token'} to your list`);
  };

  const executeTrade = async () => {
    if (!quote || !activeWallet) {
      showError('Please get a quote first and ensure wallet is connected');
      return;
    }

    try {
      setLoading(true);

      const endpoint = tradeType === 'buy' ? '/trading/manual-buy' : '/trading/manual-sell';
      const payload = tradeType === 'buy' 
        ? {
            tokenMint: toToken.mint,
            solAmount: parseFloat(fromAmount),
            walletId: activeWallet.id
          }
        : {
            tokenMint: fromToken.mint,
            tokenAmount: parseFloat(fromAmount),
            walletId: activeWallet.id
          };

      const data = await makeAuthenticatedRequest(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      // Reset form
      setFromAmount('');
      setToAmount('');
      setQuote(null);
      
      // Refresh wallet balance after successful trade
      setTimeout(() => {
        refreshBalance();
      }, 2000); // Wait 2 seconds for blockchain confirmation
      
      showTransaction(tradeType, fromToken.symbol, fromAmount, data.data.transaction.signature);
    } catch (error) {
      showError(`Failed to execute trade: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const localFilterTokens = (tokensList, query) => {
    const q = query.toLowerCase();
    return tokensList.filter(t =>
      (t.symbol && t.symbol.toLowerCase().includes(q)) ||
      (t.name && t.name.toLowerCase().includes(q))
    );
  };

  const filteredTokens = searchQuery 
    ? localFilterTokens(availableTokens, searchQuery)
    : availableTokens;

  // Mint address search preview
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setSearchError('');
      setSearchPreviewToken(null);
      setSearchPreviewPrice(null);
      if (!searchQuery) return;
      if (!tokenService.isValidMintAddress(searchQuery)) return;
      try {
        setIsSearchingToken(true);
        const meta = await tokenService.getTokenMetadata(searchQuery);
        if (!cancelled) setSearchPreviewToken(meta);
        // Fetch price for preview
        const p = await tokenService.getTokenPrice(searchQuery);
        if (!cancelled) setSearchPreviewPrice(p);
      } catch (e) {
        if (!cancelled) setSearchError('Could not load token metadata');
      } finally {
        if (!cancelled) setIsSearchingToken(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [searchQuery]);

  return (
    <div className="jupiter-trading-interface">
      {/* Version indicator for debugging */}
      {process.env.NODE_ENV === 'development' && (
        <div style={{
          position: 'absolute',
          top: '5px',
          right: '5px',
          background: '#00d4ff',
          color: '#0a0e1a',
          padding: '2px 6px',
          borderRadius: '3px',
          fontSize: '10px',
          fontWeight: 'bold',
          zIndex: 1000
        }}>
          v2.0 Enhanced
        </div>
      )}
      
      {/* Compact Header */}
      <div className="trading-header">
        <div className="price-info">
          <div className="token-info">
            <img src={fromToken?.logoURI} alt={fromToken?.symbol} className="token-logo" />
            <div className="token-details">
              <span className="token-symbol">{fromToken?.symbol}</span>
              {priceData && typeof priceData.price === 'number' && (
                <span className="token-price">
                  ${priceData.price.toFixed(2)}
                  {typeof priceData.change24h === 'number' && (
                    <span className={`price-change ${priceData.change24h >= 0 ? 'positive' : 'negative'}`}>
                      {priceData.change24h >= 0 ? '+' : ''}{priceData.change24h.toFixed(2)}%
                    </span>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
        
        <div className="trade-type-toggle">
          <button 
            className={`toggle-btn ${tradeType === 'buy' ? 'active' : ''}`}
            onClick={() => setTradeType('buy')}
          >
            Buy
          </button>
          <button 
            className={`toggle-btn ${tradeType === 'sell' ? 'active' : ''}`}
            onClick={() => setTradeType('sell')}
          >
            Sell
          </button>
        </div>
      </div>

      {/* Main trading interface */}
      <div className="trading-main">
        <div className="trading-form">
          {/* From token input */}
          <div className="token-input-container">
            <div className="input-header">
              <span className="input-label">You pay</span>
              {activeWallet && fromToken && (
                <div className="balance-info">
                  <div className="balance-row">
                    <span className="balance" onClick={setMaxAmount} title="Click to use max amount">
                      Balance: {getTokenBalance(fromToken).toFixed(4)} {fromToken.symbol}
                    </span>
                    <button 
                      className={`refresh-balance-btn ${isRefreshingBalance ? 'refreshing' : ''}`}
                      onClick={refreshBalance}
                      disabled={isRefreshingBalance}
                      title="Refresh balance"
                    >
                      🔄
                    </button>
                  </div>
                  <SafeBalanceDisplay
                    tokenMint={fromToken.mint}
                    compact={true}
                    onClick={() => setMaxAmount()}
                    className="trading-balance"
                  />
                </div>
              )}
            </div>
            <div className="token-input">
              <div className="token-selector" onClick={() => openTokenSelector('from')}>
                {fromToken ? (
                  <>
                    <img src={fromToken.logoURI} alt={fromToken.symbol} className="token-icon" />
                    <span className="token-symbol">{fromToken.symbol}</span>
                    <svg className="dropdown-icon" width="10" height="6" viewBox="0 0 12 8">
                      <path d="M6 8L0 2L1.5 0.5L6 5L10.5 0.5L12 2L6 8Z" fill="currentColor"/>
                    </svg>
                  </>
                ) : (
                  <span className="select-token">Select token</span>
                )}
              </div>
              <div className="amount-input">
                <input
                  type="number"
                  value={fromAmount}
                  onChange={(e) => handleFromAmountChange(e.target.value)}
                  placeholder="0.00"
                  className={`amount-field ${
                    fromAmount && parseFloat(fromAmount) > getAvailableBalance(fromToken) 
                      ? 'insufficient-balance' 
                      : ''
                  }`}
                />
                <div className="amount-buttons" 
                style={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
                  <button className="amount-btn" onClick={setQuarterAmount} title="25% of balance">
                    25%
                  </button>
                  <button className="amount-btn" onClick={setHalfAmount} title="50% of balance">
                    50%
                  </button>
                  <button className="amount-btn" onClick={setThreeQuarterAmount} title="75% of balance">
                    75%
                  </button>
                  <button className="amount-btn" onClick={setMaxAmount} title="Maximum available amount">
                    MAX
                  </button>
                </div>
              </div>
            </div>
            {priceData && typeof priceData.price === 'number' && fromAmount && (
              <div className="usd-value">
                ≈ ${(parseFloat(fromAmount || 0) * priceData.price).toFixed(2)} USD
              </div>
            )}
            
            {/* Balance validation */}
            {fromAmount && parseFloat(fromAmount) > 0 && (
              <BalanceValidator
                tokenMint={fromToken.mint}
                amount={parseFloat(fromAmount)}
                includeFees={true}
                showDetails={true}
                className="trading-validation"
              />
            )}
          </div>

          {/* Swap button */}
          <div className="swap-button-container">
            <button 
              className={`swap-button ${isSwapping ? 'swapping' : ''}`}
              onClick={swapTokens}
              disabled={!toToken}
            >
              <svg className="swap-icon" width="16" height="16" viewBox="0 0 16 16">
                <path d="M8 0L12 4H9V12H7V4H4L8 0Z" fill="currentColor"/>
                <path d="M8 16L4 12H7V4H9V12H12L8 16Z" fill="currentColor"/>
              </svg>
            </button>
          </div>

          {/* To token input */}
          <div className="token-input-container">
            <div className="input-header">
              <span className="input-label">You receive</span>
              {activeWallet && toToken && (
                <span className="balance">
                  Balance: {getTokenBalance(toToken).toFixed(4)} {toToken.symbol}
                </span>
              )}
            </div>
            <div className="token-input">
              <div className="token-selector" onClick={() => openTokenSelector('to')}>
                {toToken ? (
                  <>
                    <img src={toToken.logoURI} alt={toToken.symbol} className="token-icon" />
                    <span className="token-symbol">{toToken.symbol}</span>
                    <svg className="dropdown-icon" width="10" height="6" viewBox="0 0 12 8">
                      <path d="M6 8L0 2L1.5 0.5L6 5L10.5 0.5L12 2L6 8Z" fill="currentColor"/>
                    </svg>
                  </>
                ) : (
                  <span className="select-token">Select token</span>
                )}
              </div>
              <div className="amount-input">
                <input
                  type="number"
                  value={toAmount}
                  onChange={(e) => handleToAmountChange(e.target.value)}
                  placeholder="0.00"
                  className={`amount-field ${showQuoteAnimation ? 'loading' : ''}`}
                />
              </div>
            </div>
            {toTokenPrice && typeof toTokenPrice.price === 'number' && toAmount && (
              <div className="usd-value">
                ≈ ${(parseFloat(toAmount || 0) * toTokenPrice.price).toFixed(2)} USD
              </div>
            )}
          </div>

          {/* Compact Slippage settings */}
          <div className="slippage-container">
            <div className="slippage-content">
              <span className="slippage-label">Slippage: {slippage}%</span>
              <div className="slippage-options">
                {[0.1, 0.5, 1.0, 2.0].map(value => (
                  <button
                    key={value}
                    className={`slippage-btn ${slippage === value ? 'active' : ''}`}
                    onClick={() => setSlippage(value)}
                  >
                    {value}%
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Compact Quote details */}
          {quote && fromToken && toToken && (
            <div className="quote-details">
              <div className="quote-row">
                <span>Rate</span>
                <span>1 {fromToken.symbol} = {(quote.outputAmount / quote.inputAmount).toFixed(4)} {toToken.symbol}</span>
              </div>
              {quote.priceImpact > 0.1 && (
                <div className="quote-row">
                  <span>Price Impact</span>
                  <span className={quote.priceImpact > 5 ? 'high-impact' : ''}>
                    {quote.priceImpact?.toFixed(2)}%
                  </span>
                </div>
              )}
              <div className="quote-row">
                <span>Min. Received</span>
                <span>{(quote.outputAmount * (1 - slippage / 100) / Math.pow(10, toToken.decimals)).toFixed(4)} {toToken.symbol}</span>
              </div>
            </div>
          )}

          {/* Execute button */}
          <button 
            className={`execute-button ${tradeType} ${loading ? 'loading' : ''}`}
            onClick={executeTrade}
            disabled={!quote || !activeWallet || loading}
          >
            {loading ? (
              <div className="loading-spinner"></div>
            ) : (
              `${tradeType === 'buy' ? 'Buy' : 'Sell'} ${toToken?.symbol || 'Token'}`
            )}
          </button>
        </div>
      </div>

      {/* Token selector modal */}
      {showTokenSelector && (
        <div className="token-selector-modal" onClick={() => setShowTokenSelector(false)}>
          <div className="token-selector-content" onClick={(e) => e.stopPropagation()}>
            <div className="selector-header">
              <h3>Select a token</h3>
              <button 
                className="close-button"
                onClick={() => setShowTokenSelector(false)}
              >
                ×
              </button>
            </div>
            
            <div className="search-container">
              <input
                type="text"
                placeholder="Search by name or paste address"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
                autoFocus
              />
            </div>

            {/* Mint address preview */}
            {searchQuery && tokenService.isValidMintAddress(searchQuery) && (
              <div className="token-preview" style={{ marginBottom: '12px' }}>
                <h4>Token preview</h4>
                <div className="token-grid">
                  {isSearchingToken ? (
                    <div className="token-option">Loading...</div>
                  ) : searchError ? (
                    <div className="token-option">{searchError}</div>
                  ) : searchPreviewToken ? (
                    <div className="token-option" onClick={() => selectToken(searchPreviewToken)}>
                      <img src={searchPreviewToken.logoURI || '/images/unknown-token.png'} alt={searchPreviewToken.symbol} className="token-icon" />
                      <div className="token-info">
                        <span className="token-symbol">{searchPreviewToken.symbol || searchPreviewPrice?.symbol || 'UNKNOWN'}</span>
                        <span className="token-name">{searchPreviewToken.name || searchPreviewPrice?.name || 'Unknown Token'}</span>
                        <span className="token-name" style={{ fontSize: '0.75rem', opacity: 0.75 }}>{searchPreviewToken.mint}</span>
                        {searchPreviewPrice && (
                          <div style={{ marginTop: '4px', fontSize: '0.85rem' }}>
                            <div>Price: {searchPreviewPrice.price ? `$${Number(searchPreviewPrice.price).toFixed(6)}` : 'N/A'}</div>
                            <div>Market Cap: {searchPreviewPrice.marketCap ? `$${Number(searchPreviewPrice.marketCap).toLocaleString()}` : 'N/A'}</div>
                          </div>
                        )}
                      </div>
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        <button 
                          className="amount-btn"
                          onClick={(e) => { e.stopPropagation(); selectToken(searchPreviewToken); }}
                          title="Use this token"
                        >
                          Select
                        </button>
                        <button 
                          className="amount-btn"
                          onClick={(e) => { e.stopPropagation(); handleAddTokenToUserList(searchPreviewToken); }}
                          disabled={userTokens.some(t => t.mint === searchPreviewToken.mint)}
                          title={userTokens.some(t => t.mint === searchPreviewToken.mint) ? 'Already added' : 'Add to my tokens'}
                        >
                          {userTokens.some(t => t.mint === searchPreviewToken.mint) ? 'Added' : 'Add'}
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            )}

            {/* User tokens */}
            {userTokens.length > 0 && (
              <div className="popular-tokens">
                <h4>My tokens</h4>
                <div className="token-grid">
                  {userTokens.map((token) => (
                    <div
                      key={token.mint}
                      className="token-option"
                      onClick={() => selectToken(token)}
                    >
                      <img src={token.logoURI} alt={token.symbol} className="token-icon" />
                      <div className="token-info">
                        <span className="token-symbol">{token.symbol}</span>
                        <span className="token-name">{token.name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="popular-tokens">
              <h4>Popular tokens</h4>
              <div className="token-grid">
                {filteredTokens.map((token) => (
                  <div
                    key={token.mint}
                    className="token-option"
                    onClick={() => selectToken(token)}
                  >
                    <img src={token.logoURI} alt={token.symbol} className="token-icon" />
                    <div className="token-info">
                      <span className="token-symbol">{token.symbol}</span>
                      <span className="token-name">{token.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JupiterStyleTradingInterface;